<template>
  <panel-layout
    id="employee-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="employee">
        <h2
          class="py-6 text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none text-center dark:text-gray-100"
        >
          {{ $t('pages.employee.create.title') }}
        </h2>
      </div>
    </template>

    <o-scroll v-if="employee" class="h-full px-12 py-6">
      <div>
        <div class="flex space-x-4 mb-4">
          <o-field-group class="w-full md:w-1/2" :label="$t('general.firstName')">
            <text-field v-model="employee.firstName" :label="$t('general.firstName')" />
          </o-field-group>
          <o-field-group class="w-full md:w-1/2" :label="$t('general.lastName')">
            <text-field v-model="employee.lastName" :label="$t('general.lastName')" />
          </o-field-group>
        </div>

        <!-- Contact -->
        <div class="flex space-x-4 mb-4">
          <o-field-group
            v-slot="{ state }"
            class="w-full md:w-1/2"
            :label="$t('general.email')"
            :name="$t('general.email')"
            rules="email"
          >
            <text-field
              v-model="employee.email"
              type="email"
              icon="MailIcon"
              :state="state"
            />
          </o-field-group>
          <o-field-group class="w-full md:w-1/2" :label="$t('general.mobileCell')">
            <text-field v-model="employee.phone" icon="SmartphoneIcon" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-4">
          <o-field-group
            v-slot="{ status }"
            :label="$t('general.enabled')"
            :name="$t('general.enabled')"
            :info="$t('employee.allowAppAccessAndBookings')"
            col
            reverse
            class="w-full md:w-1/2"
          >
            <toggle-field v-model="employee.enabled" :status="status" />
          </o-field-group>

          <o-field-group
            v-slot="{ status }"
            :label="$t('general.allowOnlineBooking')"
            :name="$t('general.allowOnlineBooking')"
            :info="$t('employee.allowOnlineBookingInfo')"
            col
            reverse
            class="w-full md:w-1/2"
          >
            <toggle-field v-model="employee.allowInternet" :status="status" />
          </o-field-group>
        </div>

        <div class="flex space-x-4 mb-4">
          <o-field-group
            :label="$t('general.role')"
            :name="$t('general.role')"
            :info="$t('employee.setPermissions')"
            class="w-full md:w-1/2"
          >
            <role-select v-model="employee.role" />
          </o-field-group>
        </div>

        <div class="mt-6 pt-6 border-t">
          <p class="text-sm text-gray-800 mb-1 font-medium dark:text-gray-100">
            {{ $t('pages.employee.create.password') }}
          </p>
          <p class="text-sm text-gray-800 mb-6 dark:text-gray-100">
            {{ $t('pages.employee.create.passwordInfo') }}
          </p>

          <p class="text-sm text-gray-800 mb-1 font-medium dark:text-gray-100">
            {{ $t('pages.employee.create.managingAccess') }}
          </p>
          <p class="text-sm text-gray-800 dark:text-gray-100">
            {{ $t('pages.employee.create.managingAccessInfo') }}
          </p>
        </div>

        <div class="mt-6 pt-6 border-t pb-6">
          <p class="text-sm text-gray-800 mb-1 font-medium dark:text-gray-100">
            {{ $t('pages.employee.create.subscriptionCost') }}
          </p>
          <p class="text-sm text-gray-800 mb-2 dark:text-gray-100">
            {{ $t('pages.employee.create.subscriptionCostInfo') }}
          </p>
          <i18n-t keypath="employee.forMoreDetails" tag="p" class="text-sm text-gray-800 mb-2 dark:text-gray-100">
            <a
              href="https://ovatu.com/pricing"
              target="_blank"
              class="underline"
            >{{ $t('employee.pricing') }}</a>
          </i18n-t>

          <checkbox-field
            v-model="acceptPriceChange"
            :label="$t('pages.employee.create.acceptPriceChange')"
            label-class="font-medium text-gray-800 ml-3 text-sm dark:text-gray-100"
          />
        </div>
      </div>
    </o-scroll>

    <template #footer>
      <div v-if="employee">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              :disabled="!unsavedChanges || !valid"
              outline
              variant="primary"
              icon="save"
              class="mr-2"
              @click="save()"
            >
              {{ $t('general.save') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        {
          text: $t('general.closeWithoutSaving'),
          type: 'confirm',
          variant: undefined,
          flat: true,
          outline: true,
          callback: () => cancel(true)
        },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => save(true)
        }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import RoleSelect from '@/components/roles/elements/Select.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { OAlert } from '@/components/elements'
import { TextField, CheckboxField, ToggleField } from '@/components/fields'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const { $api, $notification, $bus, $i18n } = useNuxtApp()
const router = useRouter()

const employee = ref<any>(null)
const errors = ref(false)
const loading = ref(true)
const acceptPriceChange = ref(false)

const confirmDialog = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(employee)

const valid = computed(() => {
  return (
    employee.value.firstName &&
    employee.value.lastName &&
    employee.value.email &&
    acceptPriceChange.value
  )
})

const fetchEmployee = async () => {
  loading.value = true

  try {
    employee.value = (await $api.employee.defaults({} as any)).data
    snapshot()
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const save = async (close = false) => {
  loading.value = true
  errors.value = false

  try {
    const result = (await $api.employee.save(employee.value)).data

    employee.value = result
    snapshot()

    $notification.toast({
      title: $i18n.t('pages.employee.create.success'),
      text: $i18n.t('employee.successInfo'),
      variant: 'success',
    })

    $bus.emit.updated('Employee', result)

    if (close) {
      panel.close(true)
    } else {
      // Swap this create panel in-place to the freshly-created employee's edit view.
      panel.replace('employee/edit', { id: String(result.idEmployee) })
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    loading.value = false
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

// created
;(async () => {
  await fetchEmployee()
  loading.value = false
})()
</script>
